import { makeAutoObservable, runInAction } from "mobx";
import type { RootStore } from "@intrasync/stores";

/**
 * Shared data store for both management dashboards.
 *
 * One store, not two, because the Executive and Plant Operations views read
 * overlapping aggregates and a user moving between them should not re-fetch.
 *
 * TWO SOURCES, FETCHED INDEPENDENTLY:
 *   management-metrics   host aggregate — orders, schedules, products, yard
 *   qc-metrics           @intrasync/quality-control's own aggregate
 *
 * They fail independently too. If QC is unreachable the Executive tiles must
 * still render their real numbers, so each source carries its own error flag
 * rather than a single `hasError` that would blank both dashboards over one
 * bad request.
 */

// Service stores are loosely typed at the host boundary; we touch a documented
// subset of each response.
type AnyStore = any; // eslint-disable-line @typescript-eslint/no-explicit-any

/** The host aggregate. Mirrors ManagementMetrics on the API side. */
export interface ManagementMetrics {
  periodStart: string;
  periodEnd: string;
  backlog: { rowCount: number; units: number; value: number };
  bookings: {
    rowCount: number;
    value: number;
    target: number | null;
    attainment: number | null;
  };
  onTimeDelivery: { rowCount: number; onTimeCount: number; rate: number | null };
  margin: {
    rowCount: number;
    revenue: number;
    standardCost: number;
    value: number;
    rate: number | null;
    basis: "standard-cost";
  };
  overdueProduction: { rowCount: number };
  pourToday: {
    date: string;
    rowCount: number;
    scheduledUnits: number;
    completedUnits: number;
    rate: number | null;
  };
  yardInventory: { rowCount: number; placedCount: number };
}

/** The QC module's aggregate — the subset the Plant Ops dashboard reads. */
export interface QcMetrics {
  passRate: number | null;
  activeHolds: number;
  openDefects: number;
  breaksDueToday: number;
  pendingApprovals: number;
  openFollowUps: number;
  openByStage: Record<string, number>;
}

class DashboardStore {
  rootStore: RootStore;

  metrics: ManagementMetrics | null = null;
  qc: QcMetrics | null = null;

  /** Per-source, so one failure cannot blank the other dashboard's tiles. */
  metricsLoading = false;
  qcLoading = false;
  metricsFailed = false;
  qcFailed = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false }, { autoBind: true });
  }

  private store(name: string): AnyStore | undefined {
    return (this.rootStore as unknown as Record<string, AnyStore>)[name];
  }

  /**
   * Both aggregates in parallel. Never throws: a dashboard that throws on load
   * shows the router's error page, which tells the reader far less than a tile
   * that says which figure could not be fetched.
   */
  async loadData(): Promise<void> {
    await Promise.all([this.loadManagementMetrics(), this.loadQcMetrics()]);
  }

  async loadManagementMetrics(): Promise<void> {
    const service = this.store("managementMetricsStore");
    if (!service) {
      runInAction(() => {
        this.metricsFailed = true;
      });
      return;
    }

    runInAction(() => {
      this.metricsLoading = true;
      this.metricsFailed = false;
    });

    try {
      // find() returns ONE object, not a paginated list — the service has no
      // addressable rows. Some transports still wrap a bare object in an array.
      const result = await service.find({ query: {} });
      const payload = Array.isArray(result) ? result[0] : (result?.data ?? result);
      runInAction(() => {
        this.metrics = (payload ?? null) as ManagementMetrics | null;
        this.metricsFailed = payload == null;
      });
    } catch {
      runInAction(() => {
        this.metrics = null;
        this.metricsFailed = true;
      });
    } finally {
      runInAction(() => {
        this.metricsLoading = false;
      });
    }
  }

  async loadQcMetrics(): Promise<void> {
    const service = this.store("qcMetricsStore");
    if (!service) {
      // The QC module registers this store. Its absence means QC is not wired
      // in this build — a legitimate state, not an error to shout about, but
      // the QC tiles must not claim "no inspections yet" when they simply
      // could not ask.
      runInAction(() => {
        this.qcFailed = true;
      });
      return;
    }

    runInAction(() => {
      this.qcLoading = true;
      this.qcFailed = false;
    });

    try {
      const result = await service.find({ query: {} });
      const payload = Array.isArray(result) ? result[0] : (result?.data ?? result);
      runInAction(() => {
        this.qc = (payload ?? null) as QcMetrics | null;
        this.qcFailed = payload == null;
      });
    } catch {
      runInAction(() => {
        this.qc = null;
        this.qcFailed = true;
      });
    } finally {
      runInAction(() => {
        this.qcLoading = false;
      });
    }
  }
}

export default DashboardStore;
